import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loading } from "@/components/atoms";
import { ClientPickerModal } from "@/components/organisms";
import { AccountType } from "@/constants/accountTypes";
import { useLazyGetAccountsQuery } from "@/redux/apis/account/accountApi";
import { useGetGuestQuoteByIdQuery } from "@/redux/apis/guest/guestApi";
import { useGetClientOperatorsQuery, useLazyGetOperatorsQuery } from "@/redux/apis/operator/operatorApi";
import { useGetProfileQuery } from "@/redux/apis/profile/profileApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setActiveAccount } from "@/redux/slices/account/accountSlice";
import { activeAccountSelector } from "@/redux/slices/account/selectors";
import { clearBooking } from "@/redux/slices/clientBooking/clientBookingSlice";
import { setActiveOperator } from "@/redux/slices/operator/operatorSlice";
import { operatorIdSelector } from "@/redux/slices/operator/selectors";
import { Operator } from "@/redux/slices/operator/types";
import { profileSelector } from "@/redux/slices/profile/selectors";
import { BookingUnavailable } from "./BookingUnavailable";

// A logged-in booker is either a client (books for themselves) or an operator/staff (books OBO).
const CLIENT_TYPES: string[] = [
  AccountType.PASSENGER,
  AccountType.EXECUTIVE_ASSISTANT,
  AccountType.TRAVEL_MANAGER,
  "ea", // legacy alias (see AuthCallback)
];
const OPERATOR_TYPES: string[] = [AccountType.OPERATOR, AccountType.USER_ACCOUNT];

const NOT_YOUR_OPERATOR = "This booking link is for an operator you're not registered with. Ask them for a new quote link.";

/**
 * Widget quote hand-off for an ALREADY-LOGGED-IN user. The public `/guest/booking` route has none of the
 * authenticated setup (no active account / operator), so this establishes the active account, reads the
 * user's role from the profile, resolves the quote's operator, and routes into the authenticated booking
 * flow — prefilled from the quote via the destination's `useHydrateBookingFromQuote`:
 *
 *   - client  → `/client/booking?quote_uuid=…` (self-service), if they belong to the quote's operator
 *   - operator/staff → pick a client (`ClientPickerModal`) → `/operator/:id/clients/:clientId/booking?quote_uuid=…`
 *   - driver  → `/driver-app` (drivers can't book on the web)
 *
 * If the user does NOT belong to the quote's operator (a client not registered with it, or an operator
 * whose operators don't include it), it shows an error instead of prefilling — the quote's vehicles /
 * pricing wouldn't apply to a different operator.
 */
export const AuthedWidgetRedirect = ({ quoteUuid }: { quoteUuid?: string }) => {
  const dispatch = useAppDispatch();
  const activeAccount = useAppSelector(activeAccountSelector);
  const accountReady = !!activeAccount?.id;
  const [getAccounts] = useLazyGetAccountsQuery();

  // Can't determine the account (no primary, or the request failed) — there's no role to route on, so
  // surface the error rather than spin forever.
  const [accountError, setAccountError] = useState(false);

  // Establish the active account (mirrors AuthCallback) so account-scoped profile + membership queries fire.
  useEffect(() => {
    if (accountReady) return;
    getAccounts()
      .unwrap()
      .then((res) => {
        const primary = res.data.accounts.find((a) => a.primary);
        if (primary) dispatch(setActiveAccount(primary));
        else setAccountError(true);
      })
      .catch(() => setAccountError(true));
  }, [accountReady, getAccounts, dispatch]);

  const profile = useAppSelector(profileSelector);
  const { isError: profileError } = useGetProfileQuery(undefined, { skip: !accountReady });
  const type = profile?.accountMetadata?.type;
  const isClient = !!type && CLIENT_TYPES.includes(type);
  const isOperator = !!type && OPERATOR_TYPES.includes(type);

  // Fetch the quote for its operator (provider_code). Public endpoint — works with or without a session.
  const {
    data: quote,
    isError: quoteError,
    isFetching: quoteFetching,
  } = useGetGuestQuoteByIdQuery(quoteUuid ?? "", {
    skip: !quoteUuid,
  });
  const providerCode = quote?.provider_code?.toLowerCase();

  // Membership lists — only the one matching the user's role fires.
  const { data: clientOps, isFetching: clientOpsFetching } = useGetClientOperatorsQuery(undefined, {
    skip: !accountReady || !isClient,
  });
  const [getOperators, { data: operatorOps, isFetching: operatorOpsFetching }] = useLazyGetOperatorsQuery();
  useEffect(() => {
    if (accountReady && isOperator) getOperators();
  }, [accountReady, isOperator, getOperators]);

  // Account/profile couldn't be resolved — no role to route on, so error instead of spinning forever.
  if (accountError || profileError) return <BookingUnavailable />;

  // Drivers can't book on the web (mirrors AuthCallback).
  if (type === AccountType.DRIVER) return <Navigate to="/driver-app" replace />;

  // A missing/broken quote can't resolve an operator.
  if (!quoteUuid || quoteError) return <BookingUnavailable />;

  // Wait for profile + quote before branching.
  if (!type || quoteFetching || !quote) return <Loading />;
  if (!providerCode) return <BookingUnavailable />;

  if (isClient) {
    if (clientOpsFetching || !clientOps) return <Loading />;
    const belongs = clientOps.data.operators.some((o) => o.providerCode === providerCode);
    if (!belongs) return <BookingUnavailable message={NOT_YOUR_OPERATOR} />;
    // Self-service booking hydrates from the quote (see ClientBooking's useHydrateBookingFromQuote).
    return <Navigate to={`/client/booking?quote_uuid=${encodeURIComponent(quoteUuid)}`} replace />;
  }

  if (isOperator) {
    if (operatorOpsFetching || !operatorOps) return <Loading />;
    const match = operatorOps.data.operators.find((o) => o.providerCode === providerCode);
    if (!match) return <BookingUnavailable message={NOT_YOUR_OPERATOR} />;
    return <OperatorQuoteHandoff operator={match} quoteUuid={quoteUuid} />;
  }

  // Authenticated but not a booker type (or unknown) — nothing to route to.
  return <BookingUnavailable />;
};

/**
 * Operator books on behalf of a client from a widget quote. Sets the quote's operator active (the client
 * picker's list is operator-scoped through the request path), then on selection routes to the OBO booking
 * sheet carrying the `quote_uuid` so `OperatorClientBooking` prefills the draft.
 */
const OperatorQuoteHandoff = ({ operator, quoteUuid }: { operator: Operator; quoteUuid: string }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const activeOperatorId = useAppSelector(operatorIdSelector);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    dispatch(setActiveOperator(operator));
  }, [dispatch, operator]);

  const handleSelect = (clientId: string) => {
    dispatch(clearBooking());
    navigate(`/operator/${operator.id}/clients/${clientId}/booking?quote_uuid=${encodeURIComponent(quoteUuid)}`);
  };

  // Wait until the operator is active so the picker's client query targets the right provider path.
  if (activeOperatorId !== operator.id) return <Loading />;

  return (
    <>
      <Loading />
      <ClientPickerModal
        open={!dismissed}
        onClose={() => {
          setDismissed(true);
          navigate("/");
        }}
        onSelect={handleSelect}
        providerId={operator.id}
      />
    </>
  );
};
